import { DocBreadcrumbs } from '../components/DocBreadcrumbs';
import { DocPagination } from '../components/DocPagination';
import { PagePurpose } from '../components/PagePurpose';
import { StatusBadge } from '../components/StatusBadge';
import { useCatalog } from '../useCatalog';
import {
  buildingCapabilities,
  exploringCapabilities,
  liveCapabilities,
  resolveCapabilityNote,
  type Capability,
} from '../config';
import { selfServiceTools } from '@/data/selfServiceTools';

const palette = {
  accent: '#0F6B7C',
  accentTint: '#EDF4FB',
  border: '#D4E2F4',
  surface: '#F4F7FC',
};

function StatCard({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div
      className="rounded-2xl border p-4"
      style={{ borderColor: palette.border, backgroundColor: palette.accentTint }}
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-sb-black">{value}</p>
      <p className="mt-1 text-sm text-sb-dark-grey">{detail}</p>
    </div>
  );
}

function CapabilityList({
  items,
  processCount,
  domainCount,
}: {
  items: Capability[];
  processCount: number | null;
  domainCount: number | null;
}) {
  return (
    <ul className="mt-4 space-y-3">
      {items.map((item) => {
        const note = resolveCapabilityNote(item, { processCount, domainCount });
        return (
          <li
            key={item.label}
            className="rounded-2xl border p-4"
            style={{ borderColor: palette.border, backgroundColor: palette.surface }}
          >
            <p className="text-sm font-semibold text-sb-black">{item.label}</p>
            {note && <p className="mt-1 text-sm text-sb-dark-grey">{note}</p>}
          </li>
        );
      })}
    </ul>
  );
}

export function WorkInProgressPage() {
  const { processCount, domainCount } = useCatalog();

  // Tool count comes from the frontend's own live tool config — never a
  // re-hardcoded list (retired tools already removed there).
  const toolCount = selfServiceTools.length;

  return (
    <div className="space-y-8">
      <DocBreadcrumbs currentTitle="Work in Progress" />

      <PagePurpose
        title="The honest roadmap"
        audience="All team members — stay informed about what's coming"
        description="What's live, what's being built, and what we're still exploring. Labelled plainly so you always know what you can rely on today versus what's a work in progress."
        badge="Living document"
      />

      <section className="rounded-3xl bg-white p-8 shadow-sm border" style={{ borderColor: palette.border }}>
        <p className="text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: palette.accent }}>
          Work in progress
        </p>
        <h1 className="mt-3 text-3xl font-semibold text-sb-black">Transparency over perfection</h1>
        <p className="mt-4 max-w-3xl text-base text-sb-dark-grey">
          We'd rather tell you exactly where something stands than oversell it. The stats below read
          live from the registry; the roadmap uses plain Live / Building / Exploring labels.
        </p>
      </section>

      <section className="rounded-3xl border bg-white p-6 shadow-sm" style={{ borderColor: palette.border }}>
        <h2 className="text-2xl font-semibold text-sb-black">Platform at a glance</h2>
        <p className="mt-2 text-sm text-sb-dark-grey">Live figures where the registry provides them.</p>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <StatCard
            label="Self-service tools"
            value={String(toolCount)}
            detail="Tools you can run yourself at tools.sb.nl."
          />
          <StatCard
            label="OGC API methods"
            value={processCount === null ? '—' : String(processCount)}
            detail={processCount === null ? 'Live figures temporarily unavailable.' : 'Analysis methods callable right now.'}
          />
          <StatCard
            label="Dataset domains"
            value={domainCount === null ? '—' : String(domainCount)}
            detail={domainCount === null ? 'Live figures temporarily unavailable.' : 'Domains tracked in the registry.'}
          />
        </div>
      </section>

      {/* Live */}
      <section className="rounded-3xl border bg-white p-6 shadow-sm" style={{ borderColor: palette.border }}>
        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge status="live" />
          <h2 className="text-2xl font-semibold text-sb-black">Live now</h2>
        </div>
        <p className="mt-2 text-sm text-sb-dark-grey">In production and safe to use today.</p>
        <CapabilityList items={liveCapabilities} processCount={processCount} domainCount={domainCount} />
        <div className="mt-4 rounded-2xl border p-4" style={{ borderColor: palette.border, backgroundColor: palette.surface }}>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Live self-service tools</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {selfServiceTools.map((tool) => (
              <span
                key={tool.id}
                className="rounded-full border px-3 py-1 text-xs font-semibold text-sb-black"
                style={{ borderColor: palette.border, backgroundColor: '#FFFFFF' }}
              >
                {tool.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Building */}
      <section className="rounded-3xl border bg-white p-6 shadow-sm" style={{ borderColor: palette.border }}>
        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge status="building" />
          <h2 className="text-2xl font-semibold text-sb-black">Building</h2>
        </div>
        <p className="mt-2 text-sm text-sb-dark-grey">Being worked on — not finished, don't rely on it yet.</p>
        <CapabilityList items={buildingCapabilities} processCount={processCount} domainCount={domainCount} />
      </section>

      {/* Exploring */}
      <section className="rounded-3xl border bg-white p-6 shadow-sm" style={{ borderColor: palette.border }}>
        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge status="exploring" />
          <h2 className="text-2xl font-semibold text-sb-black">Exploring</h2>
        </div>
        <p className="mt-2 text-sm text-sb-dark-grey">Ideas we're weighing, not committed to.</p>
        <CapabilityList items={exploringCapabilities} processCount={processCount} domainCount={domainCount} />
      </section>

      <DocPagination currentPageId="work-in-progress" />
    </div>
  );
}
